import {
    getAdvertisersSearch,
    getAdvertiserTags,
    getAllContractors, getDebtorsDefaultFilters,
    getInvoiceOtherServices,
    getLegalEntitySearch,
} from "@/api/revenue";
import { IOtherService, ITag } from "@/api/types/revenue";
import { getActiveUserSearch } from "@/api/user";
import projects from "@/mappings/projects";
import { filterClass } from '@/services/filters/filterClass';
import { CreatedFilters } from "@/services/filters/filters";
import { IColumnFilter, IFilter } from "@/types";
import { ITableHeader } from "@/utils";
import { createColumns, IColumnsGroup } from "@/utils/ColumnsCreator";
import { getCurrentDate, getFormatDate } from "@/utils/formatDates";
import { translateDebtorStatus } from "@/utils/translate";
import { computed, ComputedRef } from "vue";
import router from '@/router';

const TOOLTIPS = {
    averageIssue: "Средневзвешенное от времени выставления в CRM. Подсчитывается от 1 числа месяца, который следует за периодом и до даты создания счёта в CRM.",
    averagePayment: "Средневзвешенное время оплаты счетов. Учитываются только полностью оплаченные счета, кроме полного взаимозачёта.",
    averageClosingTime: "Средневзвешенное время от 1 числа месяца, который следует за периодом и до даты полного закрытия счёта.",
};
const HIDE_COLUMNS = [
    'comment',
];

export const SUMMARY_KEYS = ['avg_issued_time_crm', 'avg_payment_time', 'delay_time'];

export const useDebtors = (): { headers: ITableHeader[]; summaryHeaders: ITableHeader[]; defaultColumns: ComputedRef<string[]>; getColumns: () => IColumnsGroup[] } => {
    const headers: ITableHeader[] = [
        { text: 'Дата', value: 'created_at', align: 'left', width: '90px', sortable: true },
        { text: 'Период', value: 'period', align: 'left', width: '90px' },
        { text: 'Номер счёта', value: 'external_id', sortable: true },
        { text: 'ID счёта', value: 'invoice_id', sortable: true },
        { text: 'Контрагент', value: 'contractor', sortable: true },
        { text: 'Организация', value: 'legal_entity', sortable: true },
        { text: 'Ответственный', value: 'created_by' },
        { text: 'Проект', value: 'project', align: 'center' },
        { text: 'Выручка за период', value: 'revenue_period', align: 'right', sortable: true },
        { text: 'Сумма счёта', value: 'total', align: 'center', sortable: true },
        { text: 'Валюта', value: 'currency', align: 'center' },
        { text: 'Сумма и дата оплат', value: 'payments', align: 'center' },
        { text: 'Статус', value: 'status', align: 'center' },
        { text: 'Взаимозачёт (РУБ)', value: 'netting_sum', align: 'center', sortable: true },
        { text: 'Взаимозачёт (другое)', value: 'netting_sum_other', align: 'center' },
        { text: 'Акция', value: 'other_services', align: 'center' },
        { text: 'План погашения', value: 'payment_calendars' },
        { text: 'Примечание', value: 'comment', width: '120px' },
        { text: 'Комментарий', value: 'comment_crm', width: '200px' },
        { text: 'Рекламодатель', value: 'advertiser_id', align: 'left' },
        { text: 'Признак рекламодателя', value: 'tag' },
    ];
    const summaryHeaders: ITableHeader[] = [
        { text: 'Валюта', value: 'currency', align: 'left', fixed: true },
        { text: 'Общая выручка за период', value: 'revenue_period', align: 'right', fixed: true },
        { text: 'Сумма счетов', value: 'total', align: 'right', fixed: true },
        { text: 'Сумма платежей', value: 'payments_amount', align: 'right', fixed: true },
        { text: 'Остаток', value: 'remaining_amount', align: 'right', fixed: true },
        { text: 'Взаимозачёт (РУБ)', value: 'netting_sum', align: 'right', fixed: true },
        { text: 'Взаимозачёт (другое)', value: 'netting_sum_other', align: 'right', fixed: true },
        { text: 'Среднее время выставления', value: 'avg_issued_time_crm', align: 'right', tooltip: TOOLTIPS.averageIssue },
        { text: 'Среднее время оплаты', value: 'avg_payment_time', align: 'right', tooltip: TOOLTIPS.averagePayment },
        { text: 'Общее время закрытия', value: 'delay_time', align: 'right', tooltip: TOOLTIPS.averageClosingTime, tooltipPosition: 'left' },
    ];

    const defaultColumns = computed(() => headers.filter(h => !HIDE_COLUMNS.includes(h.value)).map(h => h.value));

    const getColumns = (): IColumnsGroup[] => {
        return createColumns(headers, defaultColumns.value);
    };

    return {
        headers,
        summaryHeaders,
        defaultColumns,
        getColumns,
    };
};

export class DebtorsFilters extends CreatedFilters {
    bffFilterItems: object = {};

    constructor() {
        super();

        (async () => await this.getDefaultItemsForFilters())();
    }

    updateQueryString(data: any): void {
        const q = router.currentRoute.query;
        const next = { ...data };
        if (q.sorting_column) {
            next.sorting_column = q.sorting_column;
        }
        if (q.sorting_direction !== undefined && q.sorting_direction !== null && q.sorting_direction !== '') {
            next.sorting_direction = q.sorting_direction;
        }
        super.updateQueryString(next);
    }

    getFilters(): IFilter[] {
        const firstMonthInYear: string = getFormatDate(
            new Date(
                new Date().getFullYear(),
                0,
                1,
            ),
            'yyyy-MM-dd',
        );

        return [
            new filterClass.Datepicker({
                col: 1,
                id: 'created',
                pickerType: 'date',
                select: [],
                label: 'Дата создания',
                range: true,
                clearable: true,
            }),
            new filterClass.Datepicker({
                col: 1,
                id: 'payment_date',
                datepicker: true,
                pickerType: 'date',
                select: [],
                label: 'Дата оплаты',
                range: true,
                clearable: true,
            }),
            new filterClass.ComboboxArray({
                col: 1,
                id: 'external_ids',
                label: 'Номер',
                select: [],
                multiPaste: true,
                array: true,
            }),
            new filterClass.ComboboxArray({
                col: 1,
                id: 'ids',
                label: 'ID CRM',
                select: [],
                array: true,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'projects',
                label: 'Проекты',
                select: [],
                itemValue: 'abbr',
                type: 'string',
                items: projects,
                template: (item: any): string => item.name || item.service_name || `${item.abbr} - ${item.text}`,
                callback: () => this.getDefaultItemsForFilters(),
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'currencies',
                label: 'Валюта',
                select: [],
                items: [],
                type: 'string',
                itemValue: 'code',
                itemText: 'code',
                isCurrencyCatalogue: true,
            }),
            new filterClass.Datepicker({
                col: 1,
                id: 'period',
                label: 'Период',
                pickerType: 'month',
                select: [firstMonthInYear, getCurrentDate('yyyy-MM-dd')],
                defaultValue: [firstMonthInYear, getCurrentDate('yyyy-MM-dd')],
                range: true,
                isMonthPicker: true,
                clearable: true,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'contractor_guids',
                apiMethod: getAllContractors,
                label: 'Контрагент',
                itemValue: 'external_id',
                itemText: 'name',
                select: [],
                items: [],
                multiple: true,
                template: (item: any): string => `${item.name}`,
                menuProps: {
                    auto: true,
                },
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'legal_entity_id',
                apiMethod: getLegalEntitySearch,
                label: 'Организация',
                itemValue: 'external_id',
                itemText: 'name',
                select: [],
                items: [],
                multiple: true,
                template: (i) => i.name,
                menuProps: {
                    auto: true,
                },
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'created_by',
                apiMethod: getActiveUserSearch,
                label: 'Ответственный',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [],
                multiple: true,
                customParams: (_, value) => ({ created_by: String(value) }),
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'advertiser_ids',
                apiMethod: getAdvertisersSearch,
                label: 'Рекламодатель',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [] as Array<{ id: number, name: string }>,
                multiple: true,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'other_service_id',
                apiMethod: getInvoiceOtherServices,
                label: 'Акции',
                itemValue: 'service_id',
                itemText: 'service_name',
                select: [],
                items: [] as IOtherService[],
                multiple: true,
                template: (item: any): string => item.name || item.service_name || `${item.abbr} - ${item.text}`,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'tag_id',
                apiMethod: getAdvertiserTags,
                label: 'Признак рекламодателя',
                itemValue: 'id',
                itemText: 'name',
                select: [],
                items: [] as ITag[],
                multiple: true,
                template: (item: any): string => item.name || item.service_name || `${item.abbr} - ${item.text}`,
            }),
            new filterClass.Multiselect({
                col: 2,
                id: 'statuses',
                label: 'Статус счёта',
                type: 'string',
                select: [],
                items: [{ id: 'new' }, { id: 'sent' }, { id: 'pending' }, { id: 'partly_paid' }, { id: 'paid' }, { id: 'overpaid' }],
                template: ({ id }) => this.translateStatus(id, translateDebtorStatus),
                itemText: ({ id }) => this.translateStatus(id, translateDebtorStatus),
                itemValue: 'id',
                noDataText: 'Нет статусов',
            }),
        ];
    }

    get periodFilter(): any {
        return this.filters.find(i => i.id === 'period');
    }

    async getDefaultItemsForFilters(): Promise<void> {
        if (Object.keys(this.bffFilterItems).length === 0) {
            this.bffFilterItems = await getDebtorsDefaultFilters();
        }
        const data = this.bffFilterItems;
        const projects = ['LR', 'LKz'];
        const projectFilter = this.filters.find(f => f.id === 'projects');
        const isFilter = projects.some(p => projectFilter?.select.includes(p));

        for (const key in data) {
            const filter = this.filters.find(f => f.id === key);
            if (filter) {
                filter.permanentItems = [];
                const items = isFilter
                    ? data[key].filter(i => projectFilter?.select?.includes(i.project))
                    : data[key];
                filter.items = items;
                filter.permanentItems = items;
            }
        }
    }
}

export const monthNamesInRussian: string[] = [
    "Январь",
    "Февраль",
    "Март",
    "Апрель",
    "Май",
    "Июнь",
    "Июль",
    "Август",
    "Сентябрь",
    "Октябрь",
    "Ноябрь",
    "Декабрь",
];

export const debtorsHeadersTable: { [key: string]: string | boolean }[] = [
    { text: 'Дата', value: 'created_at', align: 'left', width: '90px' },
    { text: 'Период', value: 'period', align: 'left', width: '90px' },
    { text: 'Номер счёта', value: 'external_id' },
    { text: 'ID счёта', value: 'invoice_id' },
    { text: 'Контрагент', value: 'contractor' },
    { text: 'Организация', value: 'legal_entity' },
    { text: 'Ответственный', value: 'created_by' },
    { text: 'Проект', value: 'project', align: 'center' },
    { text: 'Выручка за период', value: 'revenue_period', align: 'center' },
    { text: 'Сумма счёта', value: 'total', align: 'center' },
    { text: 'Валюта', value: 'currency', align: 'center' },
    { text: 'Сумма и дата оплат', value: 'payments', align: 'center' },
    { text: 'Статус', value: 'status', align: 'center' },
    { text: 'Взаимозачёт (РУБ)', value: 'netting_sum', align: 'center' },
    { text: 'Взаимозачёт (другое)', value: 'netting_sum_other', align: 'center' },
    { text: 'Акция', value: 'other_services', align: 'center' },
    { text: 'План погашения', value: 'payment_calendars' },
    { text: 'Примечание', value: 'comment', width: '120px' },
    { text: 'Рекламодатель', value: 'advertiser_id', align: 'left' },
    { text: 'Признак рекламодателя', value: 'tag' },
];

export const debtorsFiltersColumns: IColumnFilter[] = [
    {
        name: '',
        items: [
            { title: 'Дата', value: 'created_at' },
            { title: 'Период', value: 'period' },
            { title: 'Номер счёта', value: 'external_id' },
            { title: 'ID счёта', value: 'invoice_id' },
            { title: 'Контрагент', value: 'contractor' },
            { title: 'Организация', value: 'legal_entity' },
            { title: 'Ответственный', value: 'created_by' },
            { title: 'Проект', value: 'project' },
            { title: 'Выручка за период', value: 'revenue_period' },
            { title: 'Сумма счёта', value: 'total' },
        ],
    },
    {
        name: '',
        items: [
            { title: 'Валюта', value: 'currency' },
            { title: 'Сумма и дата оплат', value: 'payments' },
            { title: 'Статус', value: 'status' },
            { title: 'Взаимозачёт (РУБ)', value: 'netting_sum' },
            { title: 'Взаимозачёт (другое)', value: 'netting_sum_other' },
            { title: 'Акция', value: 'other_services' },
            { title: 'План погашения', value: 'payment_calendars' },
            { title: 'Примечание', value: 'comment' },
            { title: 'ID рекламодателя', value: 'advertiser_id' },
            { title: 'Признак рекламодателя', value: 'tag' },
        ],
    },
];

export const headers: { [key: string]: string | boolean }[] = [
    { text: 'Валюта', value: 'currency', align: 'left' },
    { text: 'Общая выручка за период', value: 'revenue_period', align: 'right' },
    { text: 'Сумма счетов', value: 'total', align: 'right' },
    { text: 'Сумма платежей', value: 'payments_amount', align: 'right' },
    { text: 'Остаток', value: 'remaining_amount', align: 'right' },
    { text: 'Взаимозачёт (РУБ)', value: 'netting_sum', align: 'right' },
    { text: 'Взаимозачёт (другое)', value: 'netting_sum_other', align: 'right' },
];

export const numberFormatting = [
    {
        slot: 'item.revenue_period',
        key: 'revenue_period',
    },
    {
        slot: 'item.total',
        key: 'total',
    },
    {
        slot: 'item.payments_amount',
        key: 'payments_amount',
    },
    {
        slot: 'item.netting_sum',
        key: 'netting_sum',
    },
    {
        slot: 'item.netting_sum_other',
        key: 'netting_sum_other',
    },
    {
        slot: 'item.remaining_amount',
        key: 'remaining_amount',
    },
];
